<template>
  <v-dialog
    max-width="800px"
    fullscreen
    v-model="active"
    color="secondary"
    persistent
  >
    <v-card>
      <v-card-title class="headline grey">
        {{ editing ? "Edit an Existing Generator" : "Create a New Generator" }}
      </v-card-title>

      <v-card-actions>
        <v-container>
          <v-text-field
            label="Name"
            v-model="generator.name"
            :disabled="editing"
          >
          </v-text-field>
          <v-select
            label="Function"
            :items="generatorTypes"
            v-model="generator.generator"
          >
          </v-select>
          <v-container v-show="generator.generator == 'Gradient'">
            <div class="text-caption">Speed</div>
            <v-slider
              v-model="generator.rate"
              min="0"
              max="5"
              step="0.1"
              label="Speed"
              thumb-label="always"
            />

            <div class="text-caption">Density</div>
            <v-slider
              v-model="generator.density"
              min="0"
              max="5"
              step="0.1"
              label="Density"
              thumb-label="always"
            />
          </v-container>
          <v-container v-if="generator.generator == 'Particle'">
            <div class="text-caption">
              Number of Particles
              <v-slider
                label=" Number of Particles"
                thumb-label="always"
                v-model="generator.particleCount"
                min="1"
                max="300"
                step="1"
              >
              </v-slider>
            </div>

            <div class="text-caption">
              Particle Lifetime
              <v-slider
                label="Particle Lifetime"
                thumb-label="always"
                v-model="generator.particleLife"
                min="0.1"
                max="10"
                step="0.1"
              >
              </v-slider>
            </div>

            <div class="text-caption">
              Size
              <v-slider
                label="Range of Influence"
                thumb-label="always"
                v-model="generator.particleRange"
                min="1"
                max="300"
                step="1"
              >
              </v-slider>
            </div>

            <div class="text-caption">
              Distance Falloff
              <v-slider
                label="Curve Factor"
                thumb-label="always"
                v-model="generator.curveFactor"
                min="0.1"
                max="10"
                step="0.1"
              >
              </v-slider>
            </div>

            <div class="text-caption">
              Persistence
              <v-slider
                label="Persistence"
                thumb-label="always"
                v-model="generator.persistence"
                min="0"
                max="1"
                step="0.01"
              >
              </v-slider>
            </div>

            <v-select
              label="Intensity Method"
              :items="intensityMethods"
              v-model="generator.intensityMethod"
            >
            </v-select>

            <div
              v-if="generator.intensityMethod == 'Pulse'"
              class="text-caption"
            >
              Number of Pulses
              <v-slider
                label="Number of Pulses"
                thumb-label="always"
                v-model="generator.intensityValue"
                min="1"
                max="30"
                step="0.1"
                v-if="generator.intensityMethod == 'Pulse'"
              />
            </div>

            <v-select
              label="Color Method"
              :items="colorMethods"
              v-model="generator.colorMethod"
            />

            <v-select
              label="Initial Position Method"
              :items="attributeInitMethods"
              v-model="generator.posMethod"
            />
            <div class="text-caption">Position</div>
            <v-slider
              label="Position"
              thumb-label="always"
              v-model="generator.posRange[0]"
              min="0"
              max="900"
              step="1"
              v-if="generator.posMethod == 'Set'"
            />
            <v-range-slider
              label="Position Range"
              thumb-label="always"
              v-model="generator.posRange"
              min="0"
              max="900"
              step="1"
              v-if="generator.posMethod == 'Random in Range'"
            ></v-range-slider>

            <v-select
              label="Initial Velocity Method"
              :items="attributeInitMethods"
              v-model="generator.velMethod"
            >
            </v-select>
            <div class="text-caption">Velocity</div>
            <v-slider
              label="Velocity"
              thumb-label="always"
              v-model="generator.velRange[0]"
              min="-2"
              max="2"
              step="0.1"
              v-if="generator.velMethod == 'Set'"
            />
            <v-range-slider
              label="Velocity Range"
              thumb-label="always"
              v-model="generator.velRange"
              min="-5"
              max="5"
              step="0.1"
              v-if="generator.velMethod == 'Random in Range'"
            ></v-range-slider>

            <v-select
              label="Calculated Attribute"
              :items="AttributesToCalculate"
              v-model="generator.calculatedAttribute"
            >
            </v-select>
            <v-select
              label="Calculation Method"
              :items="AttributeCalcMethods"
              v-model="generator.attributeMethod"
            >
            </v-select>
            <v-slider
              v-if="
                generator.attributeMethod == 'Constant' ||
                generator.attributeMethod == 'Attractor'
              "
              label="Attribute Value"
              thumb-label="always"
              v-model="generator.aRange[0]"
              :min="generator.calculatedAttribute == 'Position' ? 0 : -1"
              :max="generator.calculatedAttribute == 'Position' ? 900 : 1"
              step="0.01"
            >
            </v-slider>
            <v-range-slider
              v-if="generator.attributeMethod == 'Scaled over Life'"
              label="Attribute Range"
              thumb-label="always"
              v-model="generator.aRange"
              :min="generator.calculatedAttribute == 'Position' ? 0 : -5"
              :max="generator.calculatedAttribute == 'Position' ? 900 : 5"
              step="0.01"
            >
            </v-range-slider>
            <v-slider
              v-if="generator.attributeMethod == 'Attractor'"
              label="Attraction Amount"
              thumb-label="always"
              v-model="generator.aRange[1]"
              :min="-0.1"
              :max="0.1"
              step="0.001"
            >
            </v-slider>
          </v-container>
          <v-divider></v-divider>
          <v-row class="pt-8">
            <v-btn variant="outlined" color="accent" @click="$emit('cancel')"
              >Cancel</v-btn
            >
            <v-btn
              v-if="editing"
              color="error"
              @click="$emit('delete', generator.name)"
              >Delete</v-btn
            >
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              variant="contained-text"
              @click="createGenerator(generator)"
              :disabled="generator.name?.length == 0"
            >
              {{ editing ? "Modify Generator" : "Create Generator" }}
            </v-btn>
          </v-row>
        </v-container>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  props: {
    editing: Boolean,
    propGen: Object,
  },
  created() {
    if (this.editing) {
      this.generator = JSON.parse(JSON.stringify(this.propGen));
    } else {
      this.initDefaultGenerator();
    }
  },
  data: () => ({
    generator: {},
    active: true,
    generatorTypes: ["Gradient", "Particle"],
    intensityMethods: ["Fade In", "Fade Out", "Fade In and Out", "Pulse"],
    colorMethods: ["Age", "Intensity", "Influence", "Random"],
    attributeInitMethods: ["Set", "Random in Range"],
    AttributesToCalculate: ["Position", "Velocity", "Acceleration"],
    AttributeCalcMethods: ["Constant", "Scaled over Life", "Attractor"],
  }),
  methods: {
    
    initDefaultGenerator() {
      this.generator = {
        rate: 50, //speed
        density: 50, //density
        name: "", //title
        generator: "", //generator
        particleCount: 0, //particle count
        particleLife: 0, //particle life
        particleRange: 0, //particle range
        curveFactor: 5, //curve factor
        persistence: 0, //persistence of vision
        intensityMethod: "Fade In and Out", //intensity method
        intensityValue: 1, //intensity value
        colorMethod: "Influence", //color method
        posMethod: "Random in Range", //pos init method
        posRange: [0, 900], //pos range
        velMethod: "Random in Range", //velocity init method
        velRange: [0, 1], //velocity range
        calculatedAttribute: "Acceleration", //calculated attribute
        attributeMethod: "Constant", //attribute method
        aValue3: 0,
        aRange: [0, 1], //attribute range
      };
    },
    createGenerator(gen) {
      console.log(gen);
      var genString = JSON.stringify(gen);

      this.$emit("submit", genString);

      // if (this.editing) {
      //   this.sheetsGeneratorUpdate(gen);
      // } else {
      //   this.sheetsGeneratorInsert(gen);
      // }
      // this.database.presets[gen.name] = tempTable;
      // this.presetCreationDialog = false;
      // this.setGenerator(nPre.name);
    },
  },
};
</script>